import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IReducers } from '../../store/reducers';
import { Store } from '@ngrx/store';
import { SessionService } from '../../store/services/session.service';
import { CampaignService } from 'app/store/services/campaign.service';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { IUserSessionData, UserType } from '../../store/models/user.model';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';

import * as moment from 'moment';
import * as moment_tz from 'moment-timezone';
import { openSnackbar } from '../../store/actions/page.action';
import { IBlockedDate, IBlockedDateRule, IBlockedDateRules } from '../../store/models/campaigns.model';

export interface IEditBlockedDateData {
  state: 'new' | 'edit';
}

export enum EBlockedDateType {
  single = 'single',
  range = 'range',
}

@Component({
  selector: 'app-edit-blocked-date',
  templateUrl: './edit-blocked-date.component.html',
  styleUrls: ['./edit-blocked-date.component.scss'],
})
export class EditBlockedDateComponent implements OnInit, OnDestroy {

  state: string;

  id: number;

  componentReady = false;

  formGroup: FormGroup;

  blockedDateTypes = EBlockedDateType;

  sessionData: IUserSessionData;

  addButtonProperties: Button = {
    text: 'Save',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    onClick: () => this.add(),
  };

  dateRules: any[] = [];

  blockDate: IBlockedDate;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly store$: Store<IReducers>,
    private readonly sessionService: SessionService,
    private readonly campaignService: CampaignService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  get date_type(): AbstractControl {
    return this.formGroup.get('date_type');
  }

  get date_end(): AbstractControl {
    return this.formGroup.get('blocked_date_end');
  }

  ngOnInit(): void {
    this.activatedRoute.data
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IEditBlockedDateData) => {
        this.state = data.state;
      });

    this.activatedRoute.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.id = +params.id;
      });

    this.sessionService.onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        if (this.state !== 'new') {
          this.campaignService.viewBlockedDate(this.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(blockedDate => this.blockDate = blockedDate);
        }
        this.sessionData = session;
        if (this.state === 'edit') {
          this.componentReady = true;
          return;
        }

        this.formGroup = new FormGroup({
          date_type: new FormControl(EBlockedDateType.single, [Validators.required]),
          blocked_date: new FormControl('', [Validators.required]),
          blocked_date_end: new FormControl('', []),
        });

        this.componentReady = true;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  changeDateType($event: MatRadioChange) {
    if ($event.value === EBlockedDateType.range) {
      this.date_end.addValidators([Validators.required]);
      return;
    }

    this.date_end.removeValidators([Validators.required]);
  }

  deleteRule($event: IBlockedDateRules) {
    this.componentReady = false;
    const targetRule = {
      ...$event,
    };

    // @ts-ignore
    targetRule.rule.blockType = targetRule.rule.block_type;

    // @ts-ignore
    delete targetRule.rule.block_type;
    this.dateRules = this.dateRules.filter(rule => {
      // @ts-ignore
      return JSON.stringify(this.sortObject(rule.rule)) !== JSON.stringify(this.sortObject(targetRule.rule));
    });
    this.cdr.detectChanges();
    this.componentReady = true;
  }

  addRule($event: IBlockedDateRules) {
    this.componentReady = false;
    this.dateRules.push({
      rule: {
        ...$event,
        // @ts-ignore
        blockType: $event.block_type,
      },
    });
    this.cdr.detectChanges();
    this.componentReady = true;
  }

  getStartDate() {
    const nowH = moment_tz.utc().get('hours');
    const nowM = moment_tz.utc().get('minutes');
    if (nowH <= 9) {
      if (nowM <= 30) {
        return moment().startOf('day').toDate();
      }
    }
    return moment().add(1, 'day').startOf('day').toDate();
  }

  getEndDate() {
    if (this.formGroup.get('blocked_date').value) {
      return moment(this.formGroup.get('blocked_date').value).add(1, 'day').toDate();
    }
    return moment().add(1, 'day').toDate();
  }

  private sortObject(obj: any) {
    const result = {};
    Object.keys(obj).sort().forEach((key: string) => {
      result[key] = obj[key];
    });

    return result;
  }

  private add() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.campaignService
      .createBlockDate(moment(this.formGroup.get('blocked_date').value).format('YYYY-MM-DD'),
        this.sessionData.user_type !== UserType.MASTER ? null : this.dateRules.map(r => r.rule),
            this.sessionData.user_type === UserType.MANAGER,
        this.formGroup.get('blocked_date_end').dirty ? moment(this.formGroup.get('blocked_date_end').value).format('YYYY-MM-DD') : null,
      )
      .subscribe(result => {
        this.store$.dispatch(openSnackbar({
          snackbarData: {
            data: {
              message: 'The date was added successfully!',
              type: 'success',
            },
          },
        }));
        this.router.navigate(['/', 'campaigns', 'block_dates']);
      }, err => this.store$.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: err.error,
            type: 'error',
          },
        },
      })));
  }
}
